<script lang="ts" setup>
/**
 * @usage Receives a question model and a list of errors from the parent
 * Validation for this component takes place in here and NOT in the parent
 * because there multiple possible error messages and areas to highlight.
 */
import { useTranslate } from '@/locales/i18n';
import FieldVue from '@/components/Forms/days-per-week/Field.vue';
import { InvalidFields } from '@/components/Forms/distance/types';
import { onMounted, reactive, shallowRef, ShallowRef, watch } from 'vue';

import { QuestionModel } from '../types';

const translate = useTranslate();
const props = defineProps<{
  question: QuestionModel;
  isChildQuestion?: boolean;
  multiQuestion?: boolean;
  errors?: any;
}>();
const emit = defineEmits(['answerChanged']);

const returnValue: ShallowRef<null | string> = shallowRef(null);

const data = reactive({
  model: props.question,
  isChildQuestion: props.isChildQuestion,
  spacing: 'mb-16',
  /**
   * @TODO Could answer be its own object {inputDistance, unit} instead of using a piped string? Would increase typing on codebase making it more readable to others?
   */
  answer: '|',
  invalidFields: InvalidFields.none,
  /**
   * not the ideal way to do this, but form models don't currently have a parameter for passing an error state,
   * so we have to get the complete list of errors [{errorMsg: string, inputId: string}] to be able to do
   * any error handling with this component
   */
  validation: { hasError: false, errorMessage: '' },
  errors: props.errors,
});

onMounted(() => {
  if (props.isChildQuestion) data.spacing = 'mb-8';
  if (props.multiQuestion) data.spacing = 'mb-4';
});

watch(
  () => props.errors,
  () => {
    data.errors = props.errors;
    const index = data.errors.findIndex((error: any) => error.inputId == data.model.id);
    if (index !== -1) {
      data.validation.hasError = true;
      validateAnswer();
      return;
    }
    data.validation.hasError = false;
  },
);

/** validation function
 * - checks the answer and determines data.validation.hasError
 * - also updates data.validation.hasError
 */
const validateAnswer = () => {
  const answer = data.answer;
  returnValue.value = answer;
  const input = answer.split('|')[0];
  const unit = answer.split('|')[1];
  let validationValue = 0;
  if (input && unit) {
    switch (unit) {
      case 'week':
        validationValue = Number(Number(input).toFixed(2));
        break;
      case 'month':
        validationValue = Number((Number(input) / 4.3).toFixed(2));
        break;
      case 'year':
        validationValue = Number((Number(input) / 52.15).toFixed(2));
        break;
    }

    if (validationValue && validationValue > 7) {
      data.validation.errorMessage =
        'This averages out to more than seven days per week. Please check your answer.';
      data.validation.hasError = true;
      data.invalidFields = InvalidFields.input;
      return false;
    } else {
      data.validation.errorMessage = '';
      data.validation.hasError = false;
      data.invalidFields = InvalidFields.none;
      return true;
    }
  }
  if (data.validation.hasError) {
    if (!input && !unit) {
      data.validation.errorMessage = translate('survey.errors.enterWeekMonthOrYear');
      data.invalidFields = InvalidFields.both;
    } else if (!input) {
      data.validation.errorMessage = translate('survey.errors.numberRequired');
      data.invalidFields = InvalidFields.input;
    } else if (!unit) {
      data.validation.errorMessage = translate('survey.errors.selectWeekMonthOrYear');
      data.invalidFields = InvalidFields.units;
    }
  }
  return false;
};

/** value updated function
 * - it updates the answer and calls the validation function so we can clear errors if necessary
 * - but it only shows the error messages if the question is included in the errors object (so preventing realtime error messages before they've even submitted the form)
 * Also triggers the answerChanged emit if the answer is valid
 */

const handleAnswerUpdated = (input: string) => {
  data.answer = input;
  if (validateAnswer()) emit('answerChanged', { id: data.model.id, answer: returnValue.value });
};
</script>
<template>
  <div :class="data.spacing" class="text-left">
    <h6 class="mb-2 font-bold whitespace-pre-line">
      {{ data.model.questionText }}
      <span v-if="data.model.mandatory && !data.isChildQuestion" class="text-red-500">*</span>
    </h6>
    <p class="mb-4">{{ data.model.helperText }}</p>
    <p
      v-if="data.validation.errorMessage.length"
      class="text-red-500 text-left font-bold mt-6 mb-1"
    >
      {{ data.validation.errorMessage }}
    </p>
    <FieldVue
      :id="data.model.id"
      :invalidFields="data.invalidFields"
      :placeholder="data.model.placeholderText ?? 'e.g. 5'"
      :value="data.answer"
      class="inlineErrorInput"
      @valueUpdated="handleAnswerUpdated"
    />
  </div>
</template>
